<template>
  <div class="main">
    <PushPermissionModal ref="pushModal" @activated="onPushActivated" />
    <FilterModal ref="filterModal" @apply="onFiltersApplied" @skip="onFiltersSkipped" />
    <div class="match-container">
      <div class="pool-select-row">
        <GroupSelector
          v-model="selectedPool"
          :pools="pools"
          @change="poolChanged()"
          style="flex: 1; margin: 10px 0 20px; max-width: none;"
        />
        <button class="filter-toggle-btn" @click="openFilterModal()" v-if="selectedPool" title="Filter">
          <img src="@/assets/images/user_list.svg">
        </button>
      </div>
      <div class="report-modal-wrapper darken-page" v-if="reportingProfile && currentProfile">
        <div class="report-modal-content" v-if="!reportSuccessful">
          <div class="report-header">
            <h2>Profil Melden</h2>
            <button class="cancel-report" @click="reportingProfile = false">
              <img src="@/assets/images/cross.svg" alt="Schließen">
            </button>
          </div>

          <p class="report-description">
            Du bist dabei, das Profil von <b>{{ currentProfile.name }}</b> zu melden.
            <br><br>
            Bitte beschreibe, aus welchem Grund du das Profil meldest. Wir versuchen, uns so schnell wie möglich um das Problem zu kümmern.
          </p>

          <div class="report-input-wrapper">
            <textarea
              v-model="reportingReason"
              placeholder="Grund für die Meldung (mindestens 6 Zeichen)..."
              rows="4"
            />
            <p v-if="reportingError" class="error-text">{{ reportingError }}</p>
          </div>

          <div class="report-actions">
            <button
              class="submit-report-btn"
              @click="reportProfile()"
              :disabled="reportSending || !reportingReason || reportingReason.trim().length < 6"
            >
              <span v-if="reportSending">Wird gesendet...</span>
              <span v-else>Profil melden</span>
            </button>
          </div>
        </div>

        <div class="report-modal-content success-state" v-if="reportSuccessful">
          <div class="success-icon-wrapper">
            <span class="success-checkmark">✓</span>
          </div>
          <h2>Erfolgreich gemeldet!</h2>
          <p>
            Vielen Dank dass du hilfst, gefundn. zu einem sichereren Ort für alle zu machen.
            <br><br>
            Wir haben dir eine Bestätigungsmail gesendet.
          </p>
          <button class="finish-report-btn" @click="finishReport()">
            Abschließen
          </button>
        </div>
      </div>
      <div class="match-selection-container" v-if="currentProfile && !userHasNoProfile" :class="{details_expanded: details_expanded}">
        <div class="match-info-wrapper" v-if="currentProfile" :class="{scrollable: details_expanded}">
          <div class="match-info" :class="{allow_scroll: details_expanded, details_expanded: details_expanded}">
            <div class="profile-top-row">
              <h2 id="profile">{{currentProfile.name}}</h2>
              <button class="report-btn" @click="reportingProfile = true"><img src="@/assets/images/report.svg"></button>
            </div>
            <div class="match-stat">
              <img src="@/assets/images/cake.svg">
              <p><b>{{currentProfile.age}}</b> Jahre</p>
            </div>
            <div class="match-stat">
              <img src="@/assets/images/person-height.svg">
              <p><b>{{currentProfile.height}}</b> cm</p>
            </div>
            <div class="match-stat" v-if="currentProfile.gender">
              <img src="@/assets/images/account.svg">
              <p>{{ genderLabel(currentProfile.gender) }}</p>
            </div>
            <div class="match-stat" v-if="currentProfile.looking_for">
              <img src="@/assets/images/search.svg">
              <p>{{currentProfile.looking_for}}</p>
            </div>
            <div class="match-stat" v-if="currentProfile.availability && currentProfile.availability.length > 0">
              <img src="@/assets/images/calendar.svg">
              <div class="match-dates"><p v-for="av in currentProfile.availability" :key="av.name">{{av.name}}</p></div>
            </div>
            <div class="match-stat match-note" v-if="currentProfile.short_bio">
              <img src="@/assets/images/notes.svg">
              <p>{{currentProfile.short_bio}}</p>
            </div>
            <h3>Details</h3>
            <p v-if="!currentProfile.long_bio || currentProfile.long_bio === ''"><i>Keine Beschreibung vorhanden.</i></p>
            <div class="details-container" :class="{blur: !details_expanded}" id="details">
              <p v-if="currentProfile.long_bio">{{currentProfile.long_bio}}</p>
              <div class="photos-container" id="photos">
                <div class="single-photo" v-for="(image, i) in currentProfile.images" :key="i">
                  <img v-if="image" :src="image">
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="match-actions">
          <button class="match-action no" @click="voteButton(false)"><img src="@/assets/images/cross.svg"></button>
          <button class="match-action expand-button" :class="{is_expanded : details_expanded}" @click="toggleDetails">{{details_expanded ? "Weniger" : "Mehr"}} <img src="@/assets/images/back.svg"></button>
          <button class="match-action yes" @click="voteButton(true)"><img src="@/assets/images/check.svg"></button>
        </div>
      </div>
      <div v-if="outOfProfiles" class="center-msg">
        <h3>Du hast alle Profile<br>deiner Gruppe gesehen!</h3>
      </div>
      <div v-if="userHasNoProfile" class="center-msg">
        <h3>Du musst ein Profil in dieser Gruppe erstellen, um matchen zu können.</h3>
        <router-link to="/account?create=1" class="link-button">Profil erstellen</router-link>
      </div>
      <MatchModal :show="first_match" :match-name="match ? match.name : null" :match-id="match_id" @close="first_match = false; match = null" />
      <LoadingSpinner v-if="loading" class="center-msg" message="Profile werden geladen..."/>
    </div>
  </div>
</template>

<script>
import AuthService from '@/services/AuthService.js';
import moment from 'moment';
import * as Hammer from 'hammerjs';
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import GroupSelector from "@/components/GroupSelector.vue";
import PushPermissionModal from "@/components/PushPermissionModal.vue";
import FilterModal from "@/components/FilterModal.vue";
import MatchModal from "@/components/MatchModal.vue";
export default {
  "name": "MatchView",
  data() {
    return {
      user: null,
      isAdmin: false,
      pools: [],
      selectedPool: null,
      details_expanded: false,
      nextProfiles: [],
      outOfProfiles: false,
      currentProfile: null,
      userHasNoProfile: false,
      hammertime: null,
      first_match: false,
      match_id: null,
      match: null,
      initialized: false,
      reportingProfile: false,
      reportingReason: null,
      reportingError: null,
      reportSuccessful: false,
      reportSending: false,
      loading: false,
      pushActivated: false,
      matchFilters: { gender: 'all', age_min: null, age_max: null },
    }
  },
  components: {
    LoadingSpinner,
    GroupSelector,
    PushPermissionModal,
    FilterModal,
    MatchModal
  },
  methods: {
    logout() {
      this.$store.dispatch('logout');
      this.$router.push('/login');
    },
    onPushActivated() {
      this.pushActivated = true;
    },
    async checkAndShowPushModal() {
      // Don't show if already dismissed this session
      if (sessionStorage.getItem('push_modal_dismissed')) return;
      // Don't show if push isn't supported or already denied
      if (!('PushManager' in window)) return;
      if (Notification.permission === 'denied') return;
      // Don't show if already registered in the backend
      try {
        const result = await AuthService.getPushActivationStatus();
        if (result.success && result.msg) {
          this.pushActivated = true;
          return;
        }
      } catch (e) {
        console.warn('Push-Status konnte nicht geprüft werden', e);
      }
      // Small delay so the view finishes rendering first
      setTimeout(() => {
        this.$refs.pushModal?.show();
      }, 800);
    },
    async poolChanged(){
      this.currentProfile = null
      this.nextProfiles = []
      this.outOfProfiles = false
      await this.checkIfUserHasProfileYet(this.selectedPool.id)
      if(!this.userHasNoProfile) {
        this.loadFiltersFromStorage()
        if(this.shouldShowFilterModal()) {
          this.$refs.filterModal?.show(this.matchFilters)
        } else {
          this.getNextProfiles()
        }
      }
      try {
        AuthService.updateSelectedPool({selected_pool: this.selectedPool.id})
      } catch(e) {
        console.error(e)
      }
    },
    genderLabel(gender) {
      return { male: 'Männlich', female: 'Weiblich', diverse: 'Divers' }[gender] || null
    },
    filterShownKey() {
      return 'match_filters_shown_' + this.selectedPool.id
    },
    filterStorageKey() {
      return 'match_filters_' + this.selectedPool.id
    },
    shouldShowFilterModal() {
      return !localStorage.getItem(this.filterShownKey())
    },
    loadFiltersFromStorage() {
      try {
        let stored = localStorage.getItem(this.filterStorageKey())
        this.matchFilters = stored ? JSON.parse(stored) : { gender: 'all', age_min: null, age_max: null }
      } catch(e) {
        this.matchFilters = { gender: 'all', age_min: null, age_max: null }
      }
    },
    saveFiltersToStorage() {
      localStorage.setItem(this.filterStorageKey(), JSON.stringify(this.matchFilters))
    },
    openFilterModal() {
      this.$refs.filterModal?.show(this.matchFilters)
    },
    applyFiltersAndRefetch(filters) {
      if(filters) this.matchFilters = filters
      localStorage.setItem(this.filterShownKey(), '1')
      this.saveFiltersToStorage()
      this.currentProfile = null
      this.nextProfiles = []
      this.outOfProfiles = false
      this.getNextProfiles()
    },
    onFiltersApplied(filters) {
      this.applyFiltersAndRefetch(filters)
    },
    onFiltersSkipped() {
      this.applyFiltersAndRefetch(null)
    },
    async reportProfile() {
      this.reportingError = null
      if(!this.reportingReason || this.reportingReason.length < 6) {
        this.reportingError = "Bitte gib einen Grund an."
        return -1
      }

      this.reportSending = true
      console.log(this.currentProfile)

      try {
        let result = await AuthService.reportUser({
          reported_user: this.currentProfile.user_id,
          reported_profile: this.currentProfile.profile_id,
          reported_match: null,
          report_message: this.reportingReason
        })

        if(!result.success) {
          this.reportingError = "Etwas ist schiefgelaufen."
        } else {
          this.reportSuccessful = true
        }
      } catch(e) {
        console.error(e)
        this.reportingError = e
      }

      this.reportSending = false
    },
    finishReport() {
      this.reportingProfile = false;
      this.reportingReason = null;
      this.reportSuccessful = false;
      this.castVote(false)
    },
    async castVote(like = false){
      console.log("casting vote")

      let data = {
        liked: like,
        target_id: this.currentProfile.profile_id,
        pool_id: this.selectedPool.id
      }

      try {
        this.msg = await AuthService.matchCastVote(data)
        console.log(this.msg)
        if (this.msg.success) {
          this.$store.dispatch('notifyVoteCast')

          if(this.msg.matched === true) {
            this.match = this.currentProfile
            this.first_match = true
            this.match_id = this.msg.match_id
          }

          if(this.nextProfiles.length > 0) {
            this.currentProfile = this.nextProfiles.shift()
          } else {
            this.outOfProfiles = true
            this.currentProfile = null
          }
        }
      } catch (e) {
        console.error(e)
      }

      this.initCards()
    },
    async getUserJoinedPools() {
      this.loading = true
      try {
        this.msg = await AuthService.getUserJoinedPools()
        if (this.msg.success && this.msg.msg.length > 0) {
          this.pools = this.msg.msg
          this.selectedPool = this.msg.msg.find(res => res.id === this.user.last_selected_pool) || this.msg.msg[0]
          await this.checkIfUserHasProfileYet(this.selectedPool.id)
        }
      } catch (e) {
        console.error(e)
        this.errorMsg = e.response.data.msg
      }
      this.loading = false
    },
    async checkIfUserHasProfileYet(pool_id){
      let res = await AuthService.getUserProfileFromPool({pool_id: pool_id})
      this.userHasNoProfile = !res.success || !(res.msg.length > 0)
    },
    async getNextProfiles() {
      this.loading = true

      if(this.selectedPool && this.selectedPool.id) {
          let data = {
          pool_id: this.selectedPool.id,
          gender: this.matchFilters?.gender,
          age_min: this.matchFilters?.age_min,
          age_max: this.matchFilters?.age_max
        }

        try {
          let msg = await AuthService.matchGetNextProfiles(data)
          if(msg.success){
            if(msg.msg.length === 0) {
              this.outOfProfiles = true
            } else {

              msg.msg = await Promise.all( //fetch images
                  msg.msg.map(async p => {
                    p.images = await this.loadImages(p);
                    return p;
                  })
              );

              this.nextProfiles = this.nextProfiles.concat(msg.msg.map(p => {return {...p, "age": Math.floor(moment().diff(moment(p.birthday), 'days') / 365),
                "availability": p.availability ? JSON.parse(p.availability) : []}}))

              this.nextProfiles = this.nextProfiles.filter(
                  (value, index, self) =>
                      index === self.findIndex(v => v.profile_id === value.profile_id) &&
                      value.profile_id !== (this.currentProfile ? this.currentProfile.profile_id : null)
              );

              if(!this.currentProfile) {
                this.currentProfile = this.nextProfiles.shift()
              }
            }
          }
        } catch (e) {
          console.error(e)
        }
      }
      this.loading = false
    },
    async loadImages(profile) {
      let result = await AuthService.getProfileImages({ profile_id: profile.profile_id });
      if (!result.success) return false;

      return await Promise.all(
          result.msg.map(async image_id => {
            try {
              const img = await AuthService.getImage({ image_id: image_id.image_id });
              return URL.createObjectURL(img);
            } catch (error) {
              if (error.response && error.response.status === 404) {
                console.warn(`Bild mit ID ${image_id.image_id} nicht gefunden (404).`);
                return null; // Optional: Fallback-Bild-URL hier zurückgeben
              } else {
                console.error(`Fehler beim Laden des Bildes ${image_id.image_id}:`, error);
                return null;
              }
            }
          })
      );
    },
    initCards() {
      let newCards = [document.querySelector('.match-selection-container')];
      newCards.forEach((card, index) => {
        if (card) {
          card.style.opacity = 0;
          card.style.transition = 'none';
          card.style.transform = 'scale(' + (20 - index) / 20 + ') translateY(-' + 30 * index + 'px)';
          card.classList.toggle('removed', false);
          card.offsetHeight;
          requestAnimationFrame(() => {
            card.style.transition = 'all 0.5s ease';
            requestAnimationFrame(() => {
              card.style.opacity = 1;
            });
          });
        }
      });
    },
    async voteButton(like = false) {
      let cards = [document.querySelector(".match-selection-container")]

      cards.forEach((el) => {
        let moveOutWidth = document.body.clientWidth;

        let endX = Math.max(Math.abs(2) * moveOutWidth, moveOutWidth);
        let toX = like ? endX : -endX;
        let endY = Math.abs(2) * moveOutWidth;
        let toY = -endY;
        let rotate = like ? 45 : -45

        el.style.transform = 'translate(' + toX + 'px, ' + (toY + 10) + 'px) rotate(' + rotate + 'deg)';
      })
      await this.delay(300);
      await this.castVote(like)
    },
    buildSwipeableCards(){
      let cards = [document.querySelector(".match-selection-container")]

      cards.forEach((el) => {
        if(el) {
          let hammertime = new Hammer(el);
          // only recognize horizontal drags as swipes, so vertical scrolling of the
          // expanded details still works natively even while the card stays swipeable
          hammertime.get('pan').set({ direction: Hammer.DIRECTION_HORIZONTAL, threshold: 10 });

          hammertime.on('pan', () => {
            el.classList.add('moving');
          });

          hammertime.on('pan', (event) => {
            if (event.deltaX === 0) return;
            if (event.center.x === 0 && event.center.y === 0) return;

            let xMulti = event.deltaX * 0.03;
            let yMulti = event.deltaY / 80;
            let rotate = xMulti * yMulti;

            el.style.transform = 'translate(' + event.deltaX + 'px, ' + event.deltaY + 'px) rotate(' + rotate + 'deg)';
            this.details_expanded = false
          });

          hammertime.on('panend', (event) => {
            el.classList.remove('moving');

            let moveOutWidth = document.body.clientWidth;
            let keep = Math.abs(event.deltaX) < 80 || Math.abs(event.velocityX) < 0.5;

            el.classList.toggle('removed', !keep);

            if (keep) {
              el.style.transform = '';
            } else {
              let endX = Math.max(Math.abs(event.velocityX) * moveOutWidth, moveOutWidth);
              let toX = event.deltaX > 0 ? endX : -endX;
              let endY = Math.abs(event.velocityY) * moveOutWidth;
              let toY = event.deltaY > 0 ? endY : -endY;
              let xMulti = event.deltaX * 0.03;
              let yMulti = event.deltaY / 80;
              let rotate = xMulti * yMulti;

              el.style.transform = 'translate(' + toX + 'px, ' + (toY + event.deltaY) + 'px) rotate(' + rotate + 'deg)';
              this.castVote(toX > 0)
            }
          });

          this.hammertime = hammertime
        }
      });
    },
    toggleDetails() {
      this.details_expanded = !this.details_expanded;
      const wrapper = this.$el.querySelector('.match-info-wrapper');
      if (!this.details_expanded && wrapper) {
        wrapper.scrollTop = 0;
      }
      // hammertime stays attached (restricted to horizontal drags) so swiping
      // keeps working while details are expanded and the user is scrolling vertically
    },
    delay (ms) {
      return new Promise(res => setTimeout(res, ms));
    }
  },
  async beforeCreate() {
    if (!this.$store.getters.isLoggedIn) {
      this.$router.push('/login');
    }
  },
  async mounted() {
    try {
      this.msg = await AuthService.getUser()
      this.isAdmin = !!this.msg.msg[0].isAdmin;
      this.user = this.msg.msg[0]
    } catch(e){
      console.error(e)
    }

    await this.getUserJoinedPools()
    if(this.pools.length === 0) {
      this.$router.push('/')
    }
    if (!this.userHasNoProfile) {
      this.loadFiltersFromStorage()
      if(this.shouldShowFilterModal()) {
        this.$refs.filterModal?.show(this.matchFilters)
      } else {
        await this.getNextProfiles()
      }
    }
    this.buildSwipeableCards()
    this.initialized = true;
    await this.checkAndShowPushModal();
  }
}
</script>
<style scoped>
.main {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
  padding: 20px 20px 0 20px;
}

.match-container {
  height: 100%;
  width: 100%;
  max-width: var(--max-width);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

.pool-select-row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
}

.filter-toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--glass-bg);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid var(--border-color);
  width: 44px;
  height: 44px;
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-soft);
  flex-shrink: 0;
  margin: 10px 0 20px;
}

.filter-toggle-btn img {
  width: 20px;
  height: 20px;
  filter: brightness(0) invert(1);
  opacity: 0.85;
}

/* Pool Selector */
.select-container {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--glass-bg);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  padding: 8px 16px;
  border-radius: var(--radius-xl);
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-soft);
}

.selected-pool {
  font-size: 1.1rem;
  font-weight: 600;
  font-family: var(--font);
  background: none;
  border: none;
  color: var(--text-main);
  outline: none;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
}

.select-container img {
  width: 14px;
  transform: rotate(-90deg);
  opacity: 0.5;
}

/* Match Card */
.match-selection-container {
  width: 90vw;
  max-width: var(--mid-width);
  height: 65vh;
  background: white;
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-strong);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  will-change: transform;
  transition: height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.match-selection-container.details_expanded {
  height: 70vh;
}

.match-info-wrapper {
  flex: 1;
  overflow-y: hidden;
  padding: 24px;
  text-align: left;
  overscroll-behavior: contain;
  /* Prevent text selection from blocking Hammer.js swipe detection */
  user-select: none;
  -webkit-user-select: none;
  /* let the browser handle vertical scroll natively; Hammer.js only claims horizontal drags */
  touch-action: pan-y;
}

.match-info-wrapper.scrollable {
  overflow-y: auto;
  user-select: text;
  -webkit-user-select: text;
}

.match-info-wrapper::-webkit-scrollbar {
  display: none;
}

.profile-top-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.profile-top-row h2 {
  font-size: 2.2rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  margin: 0;
}

.report-btn {
  background: #F5F5F7;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
}

.report-btn img {
  width: 100%;
  opacity: 0.4;
}

.match-stat {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  color: var(--text-secondary);
  font-size: 0.95rem;
}

.match-stat p {
  margin: 4px 0;
  color: var(--text-main);
}

.match-stat img {
  width: 18px;
  height: 18px;
  opacity: 0.6;
}

.match-dates {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.match-dates p {
  background: #F5F5F7;
  padding: 4px 10px;
  border-radius: var(--radius-s);
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-main);
}

.match-note {
  margin-top: 16px;
  padding: 16px;
  background: #F5F5F7;
  border-radius: var(--radius-m);
}

/* Details blur/expand */
.details-container {
  position: relative;
  overflow: hidden;
  max-height: 2000px;
  opacity: 1;
  transition:
    max-height 0.45s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.35s ease,
    -webkit-mask-image 0.35s ease,
    mask-image 0.35s ease;
}

.details-container > * {
  filter: blur(0px);
  transition: filter 0.35s ease;
}

.details-container.blur {
  max-height: 80px;
  pointer-events: none;
  user-select: none;
  -webkit-mask-image: linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%);
  mask-image: linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%);
  opacity: 0.6;
}

.details-container.blur > * {
  filter: blur(3px);
}

/* Actions Footer */
.match-actions {
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 20px 24px 30px;
  background: white;
  border-top: 1px solid var(--border-color);
}

.match-action {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--shadow-soft);
  transition: transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.match-action.expand-button img {
  transform: rotate(-90deg) translateX(2px);
}

.match-action.expand-button.is_expanded img {
  transform: rotate(90deg) translateX(4px);
}

.match-action:active {
  transform: scale(0.9);
}

.match-action.no {
  background: #FFF0F0;
}

.match-action.no img {
  width: 24px;
  filter: invert(27%) sepia(91%) saturate(7441%) hue-rotate(354deg) brightness(101%) contrast(114%);
}

.match-action.yes {
  background: #F0FFF4;
}

.match-action.yes img {
  width: 28px;
  filter: invert(34%) sepia(85%) saturate(1451%) hue-rotate(113deg) brightness(96%) contrast(101%);
}

button.match-action {
  width: auto;
  height: 48px;
  background: #F5F5F7;
  border-radius: var(--radius-xl);
  padding: 0 20px;
  font-weight: 600;
  color: var(--text-main);
  text-decoration: none;
  font-size: 0.9rem;
  gap: 8px;
  box-shadow: none;
}

button.match-action img {
  width: 14px;
  transition: transform 0.3s ease;
}

button.match-action.is_expanded img {
  transform: rotate(90deg);
}

/* Modal Styling */
.report-modal-content {
  background: white;
  border-radius: var(--radius-xl);
  padding: 32px 24px;
  box-shadow: var(--shadow-strong);
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: modalScaleUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-sizing: border-box;
}

@keyframes modalScaleUp {
  from { transform: scale(0.9) translateY(20px); opacity: 0; }
  to { transform: scale(1) translateY(0); opacity: 1; }
}

.report-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding-bottom: 12px;
  margin-bottom: 4px;
  width: 100%;
}

.report-header h2 {
  font-size: 1.3rem;
  font-weight: 700;
  margin: 0;
  color: var(--text-main);
}

.cancel-report {
  background: rgba(0, 0, 0, 0.05);
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.1s;
}

.cancel-report:active {
  background-color: rgba(0, 0, 0, 0.1);
  transform: scale(0.9);
}

.cancel-report img {
  width: 14px;
  height: 14px;
  opacity: 0.6;
}

.report-description {
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0;
}

.report-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.report-input-wrapper textarea {
  width: 100%;
  background: #F2F2F7;
  border: 1px solid transparent;
  border-radius: var(--radius-m);
  padding: 12px 14px;
  font-family: var(--font);
  font-size: 0.95rem;
  outline: none;
  resize: none;
  box-sizing: border-box;
  transition: all 0.2s ease;
}

.report-input-wrapper textarea:focus {
  background: white;
  border-color: #FF3B30;
  box-shadow: 0 0 0 3px rgba(255, 59, 48, 0.12);
}

.error-text {
  color: #FF3B30;
  font-size: 0.8rem;
  font-weight: 600;
  margin: 0;
}

.submit-report-btn {
  width: 100%;
  background: #FF3B30;
  color: white;
  border: none;
  padding: 14px;
  border-radius: var(--radius-m);
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.submit-report-btn:disabled {
  background: var(--text-secondary);
  opacity: 0.4;
  cursor: not-allowed;
}

.submit-report-btn:not(:disabled):active {
  transform: scale(0.98);
  background: #E02B20;
}

.success-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 24px !important;
}

.success-icon-wrapper {
  width: 60px;
  height: 60px;
  background-color: rgba(52, 199, 89, 0.12);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.success-checkmark {
  font-size: 1.8rem;
  color: #34C759;
  font-weight: bold;
}

.success-state h2 {
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
  color: var(--text-main);
}

.success-state p {
  color: var(--text-secondary);
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 12px 0 24px;
}

.finish-report-btn {
  width: 100%;
  background: var(--primary-color);
  color: white;
  border: none;
  padding: 14px;
  border-radius: var(--radius-m);
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.finish-report-btn:active {
  transform: scale(0.98);
}

.link-button {
  background: var(--primary-color);
  color: white;
  padding: 16px;
  border-radius: var(--radius-m);
  text-decoration: none;
  font-weight: 700;
  text-align: center;
}

.photos-container {
  display: flex;
  gap: 12px;
  padding: 10px 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.single-photo img {
  width: 240px;
  height: 320px;
  object-fit: cover;
  border-radius: var(--radius-m);
}

.loading-spinner {
  margin-top: 40px;
}
</style>
